/**
 * Single source of truth for Xamseen's public legal / trust pages.
 *
 * Anything that must come from the business owner is `null` here. Nothing in
 * this file invents a legal entity, address, jurisdiction, retention period,
 * certification or compliance status. Null values render as a clearly marked
 * placeholder in the UI (see <Tbd /> in components/legal-page.tsx), so an
 * unfinished item is visible instead of silently becoming a false claim.
 */

export type LegalConfig = {
  /** Registered legal entity that operates Xamseen. */
  legalEntity: string | null;
  /** Registered/postal address for legal notices. */
  postalAddress: string | null;
  /** Company or business registration number, if applicable. */
  registrationNumber: string | null;
  /** Governing law and courts for the Terms. */
  governingLaw: string | null;
  /** Data protection contact. A DPO is only named if one is actually appointed. */
  privacyContactEmail: string | null;
  dataProtectionOfficer: string | null;
  /** General support / legal contact. */
  supportEmail: string | null;
  /** Minimum age to hold an account. */
  minimumAge: number;
  /** Domain used in copy. */
  domain: string;
  /** Sending domain for transactional email. */
  emailSendingDomain: string;
  /** Last substantive review of the public legal pages. */
  lastUpdated: string;
};

export const LEGAL: LegalConfig = {
  legalEntity: null,
  postalAddress: null,
  registrationNumber: null,
  governingLaw: null,
  privacyContactEmail: null,
  dataProtectionOfficer: null,
  supportEmail: null,
  minimumAge: 16,
  domain: "xamseen.com",
  emailSendingDomain: "notify.xamseen.com",
  lastUpdated: "8 August 2026",
};

/** Everything Xamseen relies on to run, disclosed as processors. */
export const PROCESSORS: { name: string; role: string; data: string }[] = [
  {
    name: "Cloud application hosting",
    role: "Serves the Xamseen web application and runs its server-side logic.",
    data: "Requests, IP address, technical/device information needed to serve pages securely.",
  },
  {
    name: "Managed database, authentication and file storage",
    role: "Stores accounts, profiles, reports, comments, uploaded media and notifications.",
    data: "Account and profile data, authentication records, report content, uploads, notifications.",
  },
  {
    name: "Transactional email delivery",
    role: `Sends account and service email from ${LEGAL.emailSendingDomain}.`,
    data: "Email address, message content, delivery and bounce status.",
  },
  {
    name: "Automated report assessment",
    role: "Suggests a category, severity and likely responsible authority for a submitted report.",
    data: "The report text and photos you submit for assessment.",
  },
  {
    name: "Map tiles and place lookup",
    role: "Renders the civic map and turns coordinates into a readable place name.",
    data: "Map view coordinates. Requests are made from your browser to the tile/place provider.",
  },
];

/** What Xamseen does not do. Stated flatly because it is enforced by design. */
export const NEVER_DO: string[] = [
  "We do not sell your personal data.",
  "We do not rent or licence your personal data.",
  "We do not broker personal data or act as a data broker.",
  "We do not sell location data or location histories.",
  "We do not build advertising or ad-targeting profiles from your data.",
  "We do not run third-party advertising trackers, ad pixels or advertising SDKs in Xamseen.",
];

/** Items the owner must supply or confirm before public launch. */
export const LAUNCH_BLOCKERS: { key: keyof LegalConfig | string; label: string }[] = [
  { key: "legalEntity", label: "Registered legal entity operating Xamseen" },
  { key: "postalAddress", label: "Registered or postal address for legal notices" },
  { key: "registrationNumber", label: "Business registration number (if applicable)" },
  { key: "governingLaw", label: "Governing law and competent courts" },
  { key: "privacyContactEmail", label: "Privacy / data protection contact address" },
  { key: "dataProtectionOfficer", label: "Whether a DPO or representative is appointed" },
  { key: "supportEmail", label: "General support and legal contact address" },
  { key: "retention", label: "Confirmed retention periods per data category" },
];

export const LEGAL_PAGES: { to: string; label: string; blurb: string }[] = [
  { to: "/data-use", label: "How Xamseen uses data", blurb: "The plain-language version, in one page." },
  { to: "/privacy", label: "Privacy Policy", blurb: "What we collect, why, and your rights." },
  { to: "/terms", label: "Terms of Service", blurb: "The rules for using Xamseen." },
  { to: "/community-guidelines", label: "Community Guidelines", blurb: "How we expect people to behave." },
  { to: "/cookies", label: "Cookies & local storage", blurb: "What we store on your device." },
  { to: "/accessibility", label: "Accessibility", blurb: "Our commitments and known gaps." },
  { to: "/contact", label: "Contact & privacy requests", blurb: "How to reach a human." },
];
